import { classNames } from "@gettactic/helpers/src/css/classNames";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  XIcon
} from "@heroicons/react/solid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { LoadingSpinner } from "../../loadings/LoadingSpinner";
import { Scrollable } from "../../overlay/Scrollable/Scrollable";
import { Checkbox } from "@cmp-src/primitives/checkbox";
import { ModalButtons } from "@cmp-src/overlay/ModalButtons/ModalButtons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@cmp-src/primitives/Popover/Popover";

type Props = {
  options: { label: React.ReactNode; value: string; tags?: string[] }[];
  values: string[];
  onChange?: (x: string[]) => void;
  onQueryUpdate?: (x: string) => void;
  nonePlaceholder?: string;
  allPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  maxAllowed?: number;
  applyOnClick?: boolean;
  triggerMode?: "apply" | "click";
  allowAll?: boolean;
  triggerClassName?: string;
};

function updateToggleFn(item: { value: string }, maxAllowed?: number) {
  return (selected: string[]) => {
    if (selected.includes(item.value)) {
      return selected.filter((x) => x !== item.value);
    }
    if (typeof maxAllowed !== "undefined") {
      if (maxAllowed === 1) {
        return [item.value];
      }
      if (selected.length >= maxAllowed) {
        return [...selected.splice(0, 1), item.value];
      }
    }
    return [...selected, item.value];
  };
}

export function SelectMenu({
  options,
  values,
  onChange,
  onQueryUpdate,
  allPlaceholder = "All",
  nonePlaceholder = "None",
  className = "w-60",
  disabled = false,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  applyOnClick,
  maxAllowed,
  triggerMode = "apply",
  allowAll = true,
  triggerClassName
}: Props) {
  const refInput = useRef<HTMLInputElement>(null);
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(values);
  useEffect(() => {
    if (onQueryUpdate) {
      onQueryUpdate(search);
    }
  }, [search, onQueryUpdate]);
  useEffect(() => {
    if (opened) {
      setSearch("");
      setTimeout(() => refInput.current?.focus(), 400);
    } else {
      !applyOnClick && onChange?.(selected);
    }
  }, [opened, applyOnClick]);
  useEffect(() => {
    setSelected(values);
  }, [values]);

  const hasChanges = useMemo(() => {
    return (
      selected.length !== values.length ||
      selected.some((x) => !values.includes(x))
    );
  }, [selected, values]);

  useEffect(() => {
    if (hasChanges && maxAllowed === 1) {
      setOpened(false);
    }
  }, [hasChanges, maxAllowed]);

  const firstOption = values.length
    ? options.find((x) => x.value === values[0])
    : null;
  const realPlaceHolder =
    values.length === options.length &&
    !hasNextPage &&
    maxAllowed !== 1 &&
    !search.length
      ? allPlaceholder
      : values.length > 1
        ? `${values.length} selected`
        : values.length === 0
          ? nonePlaceholder
          : (firstOption?.label ?? "");
  const visibleInputs = options.filter((x) => {
    return !search.length
      ? true
      : x.tags
        ? x.tags.some((tag) => tag.toLowerCase().includes(search.toLowerCase()))
        : String(x.label).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className={className}>
      <Popover open={opened} onOpenChange={setOpened}>
        <PopoverTrigger asChild>
          {opened ? (
            <div className="relative">
              <input
                className={classNames(
                  disabled ? "cursor-disabled bg-neutral-100" : "cursor-pointer",
                  "flex w-full select-none items-center justify-between rounded-md border border-neutral-300  py-2 pl-3 focus:border-primary focus:ring-primary"
                )}
                placeholder="Search..."
                ref={refInput}
                type="search"
                value={search}
                disabled={disabled}
                onChange={(ev) => setSearch(ev.currentTarget.value)}
              />
              {search.length ? (
                <XIcon
                  onClick={() => setSearch("")}
                  className="absolute top-3 right-2 z-10 h-5 w-5 cursor-pointer text-neutral-400"
                />
              ) : null}
            </div>
          ) : (
            <div
              onClick={() => !disabled && setOpened((o) => !o)}
              className={classNames(
                disabled
                  ? "cursor-not-allowed bg-neutral-100 text-neutral-300"
                  : "cursor-pointer",
                "custom-tactic-select flex select-none items-center justify-between rounded-md border border-neutral-300 py-2 pl-3",
                triggerClassName
              )}
            >
              <span
                title={String(realPlaceHolder)}
                className="flex-1 truncate border-neutral-300 border-r leading-normal"
              >
                {realPlaceHolder}
              </span>
              <ChevronDownIcon className="h-5 w-8 text-neutral-300" />
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] select-none rounded-md p-0"
          align="start"
          showArrow={false}
        >
          <div
            className={classNames(
              typeof maxAllowed !== "undefined"
                ? "hidden"
                : "border-tacticGray-100 flex w-full justify-between space-x-4 border-b px-4 py-2 text-sm"
            )}
          >
            <div>{selected.length} selected</div>
            <div className="flex space-x-4">
              {allowAll && (
                <button
                  className="text-primary"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSelected(visibleInputs.map((x) => x.value));
                  }}
                >
                  All
                </button>
              )}

              <button
                className="text-primary"
                onClick={(ev) => {
                  ev.preventDefault();
                  setSelected(
                    options
                      .filter(
                        (x) =>
                          !visibleInputs.map((y) => y.value).includes(x.value)
                      )
                      .map((x) => x.value)
                  );
                }}
              >
                None
              </button>
            </div>
          </div>
          <Scrollable maxHeight={"300px"}>
            <div className="w-full">
              {visibleInputs.map((item) => {
                const checked = selected.includes(item.value);
                return (
                  <label
                    title={String(item.label)}
                    className={classNames(
                      "flex h-10 cursor-pointer items-center space-x-2",
                      maxAllowed === 1 ? "px-0" : "px-4",
                      maxAllowed === 1 && checked ? "text-primary" : ""
                    )}
                    key={item.value}
                    htmlFor={`checkbox-list-${item.value}`}
                  >
                    <Checkbox
                      onCheckedChange={() => {
                        if (maxAllowed === 1 && triggerMode === "click") {
                          setSelected([item.value]);
                          setOpened(false);
                          onChange?.([item.value]);
                          return;
                        }

                        setSelected(updateToggleFn(item, maxAllowed));
                      }}
                      id={`checkbox-list-${item.value}`}
                      checked={checked}
                      className={classNames(
                        maxAllowed === 1
                          ? "hidden"
                          : "rounded checked:bg-primary checked:hover:bg-primary-hover focus:ring-0 focus:ring-primary checked:focus:bg-primary"
                      )}
                    />

                    {maxAllowed === 1 ? (
                      <CheckCircleIcon
                        className={classNames(
                          "h-5 w-5 text-primary",
                          checked ? "" : "invisible"
                        )}
                      />
                    ) : null}

                    <span className="truncate">{item.label}</span>
                  </label>
                );
              })}
            </div>
          </Scrollable>
          <div
            className={classNames(
              "flex w-full justify-center space-x-4 border-tacticGray-100 px-4 text-sm transition-all duration-100",
              !search.length && hasNextPage && fetchNextPage
                ? "border-t py-2 opacity-100"
                : "h-0 opacity-0"
            )}
          >
            <button
              disabled={!hasNextPage || !fetchNextPage || isFetchingNextPage}
              className="flex items-center space-x-1 text-primary"
              onClick={(ev) => {
                ev.preventDefault();
                if (fetchNextPage) {
                  fetchNextPage();
                }
              }}
            >
              {isFetchingNextPage ? <LoadingSpinner /> : "Load more"}
            </button>
          </div>

          <ModalButtons
            onCancel={() => {
              setSelected(values);
              setOpened(false);
            }}
            onApply={() => {
              setOpened(false);
              applyOnClick && onChange?.(selected);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
